import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, Send, Loader2, Save, Eye, Image, RefreshCw, 
  MessageSquare, Sparkles, Check, X, Maximize2, Minimize2,
  Zap, Heart, Briefcase, Camera, BookOpen, Scissors, Plus,
  FileText, MapPin, Calendar, Building, Ship, ChevronDown, ChevronUp,
  AlertCircle
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Boutons d'actions rapides pour l'IA
const quickActions = [
  { id: "regenerate", label: "Regénérer", icon: RefreshCw, prompt: "Régénère entièrement ce texte en gardant le même contexte et les mêmes informations." },
  { id: "shorten", label: "Raccourcir", icon: Scissors, prompt: "Raccourcis ce texte en gardant l'essentiel. Supprime les redondances et va à l'essentiel." },
  { id: "expand", label: "Développer", icon: Plus, prompt: "Développe chaque section de l'article pour atteindre 1800 mots minimum. Enrichis les paragraphes trop courts (moins de 150 mots) avec des descriptions visuelles factuelles. Conserve les blocs image existants avec leurs légendes. N'invente rien qui ne soit pas visible sur les photos." },
  { id: "premium", label: "Ton plus premium", icon: Sparkles, prompt: "Réécris ce texte avec un ton plus luxueux, raffiné et haut de gamme. Conserve tous les blocs existants." },
  { id: "human", label: "Ton plus humain", icon: Heart, prompt: "Réécris ce texte avec un ton plus chaleureux, personnel et authentique. Conserve tous les blocs existants." },
  { id: "experience", label: "Plus orienté expérience", icon: BookOpen, prompt: "Réécris ce texte en mettant davantage l'accent sur l'expérience vécue et les moments forts. Conserve tous les blocs existants." },
  { id: "practical", label: "Plus orienté pratique", icon: Briefcase, prompt: "Ajoute des informations pratiques observables sur les photos : disposition des espaces, équipements visibles. Conserve tous les blocs existants." },
  { id: "photos", label: "Mieux valoriser les photos", icon: Camera, prompt: "Améliore les légendes des photos et la description des éléments visibles sur chaque image. Conserve tous les blocs existants." },
];

export default function AIEditorPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Context from navigation (photos, editorial info, etc.)
  const [generationContext, setGenerationContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  
  // Constantes pour la validation
  const MIN_WORDS_FOR_LONG = 1800;
  const WORD_COUNT_TYPES = ['section_title', 'paragraph', 'quote'];
  
  // Fonction pour calculer le nombre de mots depuis les content_blocks
  const calculateWordCount = (blocks) => {
    if (!blocks || blocks.length === 0) return 0;
    return blocks.reduce((total, block) => {
      if (WORD_COUNT_TYPES.includes(block.type) && block.text) {
        return total + block.text.split(/\s+/).filter(w => w.length > 0).length;
      }
      return total;
    }, 0);
  };
  
  // Content state
  const [editedContent, setEditedContent] = useState({
    title: "",
    subtitle: "",
    content: "",
    excerpt: "",
    content_blocks: [],  // Nouveaux blocs structurés magazine
    featured_image: null,
    featured_image_url: null,
    word_count: 0,
    seo_title: "",
    meta_description: ""
  });
  
  // Calculer le nombre de mots actuel (recalculé à chaque changement de content_blocks)
  const currentWordCount = calculateWordCount(editedContent.content_blocks);
  const isWordCountSufficient = currentWordCount >= MIN_WORDS_FOR_LONG;
  const wordCountProgress = Math.min(100, Math.round((currentWordCount / MIN_WORDS_FOR_LONG) * 100));
  
  const [showPreview, setShowPreview] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState(null); // 'context', 'content', 'chat'
  const [showContextPanel, setShowContextPanel] = useState(true);

  useEffect(() => {
    // Get context from navigation state
    const state = location.state;
    if (state) {
      setGenerationContext(state);
      
      // If we have existing content (editing mode)
      if (state.existingContent) {
        setEditedContent({
          title: state.existingContent.title || "",
          subtitle: state.existingContent.subtitle || "",
          content: state.existingContent.content || "",
          excerpt: state.existingContent.excerpt || "",
          content_blocks: state.existingContent.content_blocks || [],
          featured_image: state.existingContent.featured_image || null,
          featured_image_url: state.existingContent.featured_image_url || null,
          word_count: state.existingContent.word_count || 0,
          seo_title: state.existingContent.seo_title || "",
          meta_description: state.existingContent.meta_description || ""
        });
        setMessages([{
          role: "assistant",
          content: `Bonjour ! Je suis prêt à vous aider à améliorer cet article.\n\n**Titre actuel:** ${state.existingContent.title}\n\nUtilisez les boutons d'action rapide ou écrivez-moi directement vos demandes de modification.`
        }]);
      } else {
        // New generation mode - will generate on mount
        setMessages([{
          role: "assistant",
          content: `Bienvenue dans l'éditeur IA ! Je vais générer un premier brouillon basé sur vos ${state.photos?.length || 0} photos et vos indications.\n\nUne fois le brouillon créé, vous pourrez me demander des modifications via le chat ou les boutons d'action rapide.`
        }]);
        // Auto-generate initial content
        generateInitialContent(state);
      }
      setLoading(false);
    } else {
      toast.error("Aucun contexte de génération fourni");
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Cache indicator state
  const [cacheInfo, setCacheInfo] = useState(null);
  const [forceRefresh, setForceRefresh] = useState(false);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const generateInitialContent = async (context, forceNew = false) => {
    setGenerating(true);
    setCacheInfo(null);
    try {
      const requestData = {
        photo_ids: context.photos?.map(p => p.id) || [],
        category_id: context.category_id,
        destination_id: context.destination_id || undefined,
        content_type: context.content_type || "article",
        location: context.location || undefined,
        venue_name: context.venue_name || undefined,
        company: context.company || undefined,
        dates: context.dates || undefined,
        ambiance: context.ambiance || undefined,
        highlights: context.highlights || undefined,
        lowlights: context.lowlights || undefined,
        practical_info: context.practical_info || undefined,
        editorial_angle: context.editorial_angle || undefined,
        tone: context.tone || "chaleureux et personnel",
        keywords: context.keywords || undefined,
        must_mention: context.must_mention || undefined,
        length: context.length || "medium",
        seo_optimized: context.seo_optimized !== false,
        cover_photo_index: context.cover_photo_index || 0,
        return_draft_only: true,
        force_refresh: forceNew // Force bypass cache
      };

      const res = await axios.post(`${API}/ai/generate-draft`, requestData, { 
        headers: getAuthHeaders() 
      });
      
      const draft = res.data;
      setEditedContent({
        title: draft.title || "",
        subtitle: draft.subtitle || "",
        content: draft.content || "",
        excerpt: draft.excerpt || "",
        content_blocks: draft.content_blocks || [],  // Blocs structurés
        featured_image: draft.featured_image || null,
        featured_image_url: draft.featured_image_url || null,
        word_count: draft.word_count || 0,
        seo_title: draft.seo_title || "",
        meta_description: draft.meta_description || ""
      });
      
      // Store cache info for transparency
      if (draft.cache_info) {
        setCacheInfo({
          fromCache: draft.from_cache,
          ...draft.cache_info
        });
      }
      
      // Build message based on cache status
      let message = `Voici le brouillon ${draft.from_cache ? '(récupéré du cache)' : 'généré'} !\n\n**${draft.title}**\n\n`;
      
      // Ajouter le compteur de mots
      if (draft.word_count) {
        message += `📊 **${draft.word_count} mots** générés`;
        if (draft.word_count_warning) {
          message += ` ⚠️ ${draft.word_count_warning}`;
        }
        message += `\n\n`;
      }
      
      // Ajouter info sur les blocs structurés
      if (draft.content_blocks && draft.content_blocks.length > 0) {
        const imageBlocks = draft.content_blocks.filter(b => b.type === 'image' || b.type === 'image_gallery').length;
        message += `📰 **Format magazine**: ${draft.content_blocks.length} blocs dont ${imageBlocks} photos intégrées\n\n`;
      }
      
      // Avertissement si article trop court
      const wordCount = draft.word_count || 0;
      if (wordCount < MIN_WORDS_FOR_LONG) {
        message += `⚠️ **Article incomplet**: ${wordCount} mots générés, objectif ${MIN_WORDS_FOR_LONG} mots.\n`;
        message += `Demandez-moi de développer certaines sections pour atteindre le minimum requis.\n\n`;
      }
      
      if (draft.from_cache) {
        message += `📦 **Cache**: Ce résultat provient du cache (utilisé ${draft.cache_info?.hit_count || 1} fois).\nPour forcer une nouvelle génération, cliquez sur "Forcer regénération".\n\n`;
      } else {
        message += `✨ **Nouveau**: Contenu fraîchement généré par l'IA.\n\n`;
      }
      
      if (wordCount >= MIN_WORDS_FOR_LONG) {
        message += `Vous pouvez maintenant :\n- Prévisualiser l'article avec "Aperçu"\n- Me demander des modifications dans le chat\n- Cliquer sur "Intégrer au site" quand vous êtes satisfait`;
      } else {
        message += `Pour intégrer l'article au site :\n1. Demandez-moi de développer les sections (ex: "Développe la section restauration")\n2. Le bouton "Intégrer au site" sera activé une fois les ${MIN_WORDS_FOR_LONG} mots atteints`;
      }
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: message
      }]);
      
    } catch (error) {
      console.error("Generation error:", error);
      const errorMsg = error.response?.data?.detail || "Erreur lors de la génération";
      toast.error(errorMsg);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `Désolé, une erreur s'est produite lors de la génération : ${errorMsg}\n\nVous pouvez réessayer avec le bouton "Regénérer" ou ajuster vos paramètres.`
      }]);
    } finally {
      setGenerating(false);
    }
  };

  // Handler for force regeneration
  const handleForceRegenerate = () => {
    if (generationContext) {
      generateInitialContent(generationContext, true);
    }
  };

  const sendMessage = async (customPrompt = null) => {
    const messageToSend = customPrompt || inputMessage.trim();
    if (!messageToSend || chatLoading) return;
    
    if (!customPrompt) setInputMessage("");
    
    // Add user message
    setMessages(prev => [...prev, { role: "user", content: messageToSend }]);
    setChatLoading(true);
    
    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: messageToSend,
        current_content: editedContent.content || "",
        current_content_blocks: editedContent.content_blocks || [],  // Envoyer les blocs structurés
        current_title: editedContent.title,
        current_excerpt: editedContent.excerpt,
        context: {
          photos: generationContext?.photos?.map((p, i) => ({
            index: i + 1,
            filename: p.filename || p.original_filename,
            description: p.description || null
          })),
          category: generationContext?.category_name,
          destination: generationContext?.destination_name,
          venue_name: generationContext?.venue_name,
          tone: generationContext?.tone
        }
      }, { headers: getAuthHeaders() });
      
      const aiResponse = res.data;
      
      // Add AI response
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: aiResponse.message,
        suggested_changes: aiResponse.suggested_changes,
        word_count_added: aiResponse.word_count_added
      }]);
      
      // Auto-apply suggested content_blocks if present (priorité sur content legacy)
      if (aiResponse.suggested_content_blocks && aiResponse.suggested_content_blocks.length > 0) {
        console.log("=== FUSION content_blocks ===");
        console.log("Existing blocks:", editedContent.content_blocks?.length || 0);
        console.log("Suggested blocks:", aiResponse.suggested_content_blocks.length);
        
        // FUSION INTELLIGENTE : Ne pas remplacer aveuglément, mais fusionner
        // Si l'IA retourne moins de blocs, c'est qu'elle a peut-être oublié certains blocs
        // On doit préserver les blocs existants et enrichir seulement ceux modifiés
        
        setEditedContent(prev => {
          const existingBlocks = prev.content_blocks || [];
          const suggestedBlocks = aiResponse.suggested_content_blocks;
          
          console.log("Inside setEditedContent - existingBlocks:", existingBlocks.length);
          console.log("Inside setEditedContent - suggestedBlocks:", suggestedBlocks.length);
          
          // Si l'IA retourne autant ou plus de blocs, utiliser sa version (génération complète)
          // Si elle retourne moins de blocs, fusionner intelligemment
          let finalBlocks;
          
          // FUSION INTELLIGENT: Seuil 80% pour décider si on remplace ou fusionne
          const MERGE_THRESHOLD = 0.8; // Si l'IA retourne >= 80% des blocs existants, utiliser sa version
          
          if (suggestedBlocks.length >= existingBlocks.length * MERGE_THRESHOLD) {
            // L'IA a retourné une version assez complète, l'utiliser
            console.log("Using suggested blocks (>= 80% threshold)");
            finalBlocks = suggestedBlocks;
          } else {
            // L'IA a retourné moins de blocs - ajouter les nouveaux sans supprimer les existants
            finalBlocks = [...existingBlocks];
            
            // Extraire les nouveaux paragraphes/sections à ajouter
            const newContentBlocks = suggestedBlocks.filter(b => 
              b.type === 'paragraph' || b.type === 'section_title' || b.type === 'quote'
            );
            
            if (newContentBlocks.length > 0) {
              // Trouver l'index de la conclusion si elle existe
              const conclusionIdx = existingBlocks.findIndex(b => 
                b.type === 'section_title' && 
                b.text.toLowerCase().includes('conclusion')
              );
              
              if (conclusionIdx > 0) {
                // Insérer avant la conclusion
                finalBlocks = [
                  ...existingBlocks.slice(0, conclusionIdx),
                  ...newContentBlocks,
                  ...existingBlocks.slice(conclusionIdx)
                ];
              } else {
                // Ajouter à la fin
                finalBlocks = [...existingBlocks, ...newContentBlocks];
              }
            }
          }
          
          // Préserver les blocs image avec leurs photo_id/photo_url originaux
          // Le backend fait déjà ce travail, mais on s'assure côté frontend aussi
          const imageBlocksById = new Map();
          existingBlocks.forEach(block => {
            if (block.type === 'image' && block.photo_id) {
              imageBlocksById.set(block.photo_id, block);
            }
            if (block.type === 'image_gallery' && block.photo_ids) {
              block.photo_ids.forEach((id, idx) => {
                imageBlocksById.set(id, { photo_url: block.photo_urls?.[idx] });
              });
            }
          });
          
          // Restaurer les URLs des images dans les blocs finaux
          finalBlocks = finalBlocks.map(block => {
            if (block.type === 'image' && block.photo_id && !block.photo_url) {
              const original = imageBlocksById.get(block.photo_id);
              if (original) {
                return { ...block, photo_url: original.photo_url };
              }
            }
            return block;
          });
          
          return {
            ...prev,
            content_blocks: finalBlocks
          };
        });
        
        // Notification de succès avec nombre de mots ajoutés
        const wordsAdded = aiResponse.word_count_added || 0;
        if (wordsAdded > 0) {
          toast.success(`+${wordsAdded} mots ajoutés au contenu`);
        } else {
          toast.success("Contenu mis à jour");
        }
      } 
      // Fallback sur l'ancien format si pas de content_blocks
      else if (aiResponse.suggested_content) {
        const newContent = { ...editedContent };
        if (aiResponse.suggested_content.title) newContent.title = aiResponse.suggested_content.title;
        if (aiResponse.suggested_content.content) newContent.content = aiResponse.suggested_content.content;
        if (aiResponse.suggested_content.excerpt) newContent.excerpt = aiResponse.suggested_content.excerpt;
        setEditedContent(newContent);
        toast.success("Contenu mis à jour");
      }
      
    } catch (error) {
      console.error("Chat error:", error);
      console.error("Error details:", error.response?.data);
      const errorMsg = error.response?.data?.detail || "Erreur de communication avec l'IA";
      toast.error(errorMsg);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: `Désolé, une erreur s'est produite : ${errorMsg}. Réessayez votre demande.`
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    sendMessage(action.prompt);
  };

  const handleSave = async () => {
    console.log("=== handleSave START ===");
    console.log("editedContent.title:", editedContent.title);
    console.log("editedContent.content_blocks length:", editedContent.content_blocks?.length);
    console.log("currentWordCount:", currentWordCount);
    console.log("isWordCountSufficient:", isWordCountSufficient);
    
    try {
      if (!editedContent.title?.trim()) {
        toast.error("Le titre est requis");
        return;
      }
      
      // Vérifier qu'on a soit du contenu soit des content_blocks
      const hasContent = editedContent.content && editedContent.content.trim().length > 0;
      const hasBlocks = editedContent.content_blocks && editedContent.content_blocks.length > 0;
      
      console.log("hasContent:", hasContent, "hasBlocks:", hasBlocks);
      
      if (!hasContent && !hasBlocks) {
        toast.error("Le contenu est requis");
        return;
      }
      
      // Vérifier le nombre de mots minimum pour les articles longs
      if (!isWordCountSufficient) {
        toast.error(`Article incomplet : ${currentWordCount} mots sur ${MIN_WORDS_FOR_LONG} minimum. Demandez à l'IA de développer les sections avant l'intégration.`);
        return;
      }
      
      setSaving(true);
      
      // If editing existing article
      if (generationContext?.article_id) {
        console.log("Updating existing article:", generationContext.article_id);
        const updatePayload = {
          title: editedContent.title,
          subtitle: editedContent.subtitle,
          content: editedContent.content || "",
          content_blocks: editedContent.content_blocks || [],
          excerpt: editedContent.excerpt,
          featured_image: editedContent.featured_image,
          seo_title: editedContent.seo_title,
          meta_description: editedContent.meta_description,
          status: "ai_draft"
        };
        console.log("Update payload:", JSON.stringify(updatePayload, null, 2).substring(0, 500) + "...");
        
        await axios.put(`${API}/articles/${generationContext.article_id}`, updatePayload, { headers: getAuthHeaders() });
        toast.success("Article mis à jour en brouillon !");
      } else {
        // Create new article from draft
        const articleData = {
          title: editedContent.title,
          subtitle: editedContent.subtitle,
          content: editedContent.content || "",
          content_blocks: editedContent.content_blocks || [],
          excerpt: editedContent.excerpt || "",
          featured_image: editedContent.featured_image,
          seo_title: editedContent.seo_title || "",
          meta_description: editedContent.meta_description || "",
          category_id: generationContext?.category_id,
          destination_id: generationContext?.destination_id || undefined,
          photo_ids: generationContext?.photos?.map(p => p.id) || [],
          status: "ai_draft",
          venue_name: generationContext?.venue_name || "",
          company: generationContext?.company || "",
          dates: generationContext?.dates || "",
          // highlights et lowlights doivent être des arrays, pas des strings
          highlights: Array.isArray(generationContext?.highlights) 
            ? generationContext.highlights 
            : (generationContext?.highlights ? [generationContext.highlights] : []),
          lowlights: Array.isArray(generationContext?.lowlights) 
            ? generationContext.lowlights 
            : (generationContext?.lowlights ? [generationContext.lowlights] : []),
          tone: generationContext?.tone || ""
        };
        
        console.log("Creating article with category_id:", articleData.category_id);
        console.log("content_blocks count:", articleData.content_blocks?.length);
        console.log("photo_ids count:", articleData.photo_ids?.length);
        
        const response = await axios.post(`${API}/articles`, articleData, { headers: getAuthHeaders() });
        console.log("Article created successfully:", response.data?.id, response.data?.slug);
        toast.success("Article créé en brouillon !");
      }
      
      // Navigate to articles list
      console.log("Navigating to /dashboard/articles");
      navigate("/dashboard/articles");
      
    } catch (error) {
      console.error("=== handleSave ERROR ===");
      console.error("Error object:", error);
      console.error("Error message:", error.message);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      const errorMsg = error.response?.data?.detail || error.message || "Erreur lors de la sauvegarde";
      toast.error(`Erreur: ${errorMsg}`);
    } finally {
      setSaving(false);
      console.log("=== handleSave END ===");
    }
  };

  const applyAISuggestion = (suggestion) => {
    if (suggestion.field === "content") {
      setEditedContent(prev => ({ ...prev, content: suggestion.value }));
    } else if (suggestion.field === "title") {
      setEditedContent(prev => ({ ...prev, title: suggestion.value }));
    } else if (suggestion.field === "excerpt") {
      setEditedContent(prev => ({ ...prev, excerpt: suggestion.value }));
    }
    toast.success("Suggestion appliquée !");
  };

  const getPhotoUrl = (photo) => {
    if (photo?.cloudinary_url) {
      return photo.cloudinary_url.replace('/upload/', '/upload/c_fill,g_auto,w_200,h_200,f_auto,q_auto/');
    }
    if (photo?.image_data) return `data:image/jpeg;base64,${photo.image_data}`;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 p-6">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-12 w-64 mb-4" />
          <Skeleton className="h-[600px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="ai-editor-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-20 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Retour
            </Button>
            <div>
              <h1 className="font-heading text-lg font-semibold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Éditeur IA Interactif
              </h1>
              <p className="text-sm text-muted-foreground">
                {generationContext?.article_id ? "Modification" : "Nouvelle génération"} • 
                {generationContext?.photos?.length || 0} photos
              </p>
            </div>
          </div>
          
          {/* Word count indicator */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span className={`font-medium ${isWordCountSufficient ? 'text-green-600' : 'text-orange-600'}`}>
                {currentWordCount} / {MIN_WORDS_FOR_LONG} mots
              </span>
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${isWordCountSufficient ? 'bg-green-500' : 'bg-orange-500'}`}
                  style={{ width: `${wordCountProgress}%` }}
                />
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowPreview(!showPreview)}
              data-testid="preview-toggle"
            >
              <Eye className="h-4 w-4 mr-2" /> 
              {showPreview ? "Éditeur" : "Aperçu"}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || generating || !isWordCountSufficient}
              className={`${isWordCountSufficient ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
              data-testid="save-btn"
              title={!isWordCountSufficient ? `Article incomplet : ${currentWordCount}/${MIN_WORDS_FOR_LONG} mots` : "Intégrer l'article au site"}
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Enregistrement...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" /> Intégrer au site</>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {/* Alert if word count insufficient */}
        {!isWordCountSufficient && editedContent.content_blocks.length > 0 && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-orange-800">
                Article incomplet : {currentWordCount} mots sur {MIN_WORDS_FOR_LONG} minimum
              </p>
              <p className="text-sm text-orange-700 mt-1">
                Demandez à l'IA de développer les sections en utilisant le chat ci-dessous. 
                Par exemple : "Développe la section sur les espaces extérieurs" ou "Ajoute plus de détails sur la restauration".
              </p>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-12 gap-4" style={{ minHeight: "calc(100vh - 180px)" }}>
          
          {/* Left Panel - Context Source */}
          {showContextPanel && (
            <div className={`${expandedPanel === 'context' ? 'col-span-12' : 'col-span-3'} bg-white rounded-lg border overflow-hidden flex flex-col`}>
              <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
                <span className="font-medium text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  Contexte source
                </span>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7"
                    onClick={() => setExpandedPanel(expandedPanel === 'context' ? null : 'context')}
                  >
                    {expandedPanel === 'context' ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7"
                    onClick={() => setShowContextPanel(false)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto p-4 space-y-4">
                {/* Photos */}
                {generationContext?.photos?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase mb-2">
                      Photos sélectionnées ({generationContext.photos.length})
                    </p>
                    <div className={`grid ${expandedPanel === 'context' ? 'grid-cols-8' : 'grid-cols-3'} gap-2`}>
                      {generationContext.photos.map((photo, idx) => (
                        <div 
                          key={photo.id} 
                          className={`relative aspect-square rounded overflow-hidden ${
                            idx === (generationContext.cover_photo_index || 0) ? 'ring-2 ring-purple-500' : ''
                          }`}
                        >
                          <img 
                            src={getPhotoUrl(photo)} 
                            alt={photo.filename} 
                            className="w-full h-full object-cover"
                          />
                          {idx === (generationContext.cover_photo_index || 0) && (
                            <span className="absolute bottom-0 left-0 right-0 bg-purple-600 text-white text-[10px] text-center py-0.5">
                              Couverture
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Editorial Info */}
                <div className="space-y-3">
                  {generationContext?.category_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Ship className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Catégorie:</span>
                      <span className="font-medium">{generationContext.category_name}</span>
                    </div>
                  )}
                  {generationContext?.destination_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Destination:</span>
                      <span className="font-medium">{generationContext.destination_name}</span>
                    </div>
                  )}
                  {generationContext?.venue_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Établissement:</span>
                      <span className="font-medium">{generationContext.venue_name}</span>
                    </div>
                  )}
                  {generationContext?.dates && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Dates:</span>
                      <span className="font-medium">{generationContext.dates}</span>
                    </div>
                  )}
                  {generationContext?.tone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Heart className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Ton:</span>
                      <span className="font-medium">{generationContext.tone}</span>
                    </div>
                  )}
                </div>
                
                {/* Highlights/Lowlights */}
                {generationContext?.highlights && (
                  <div>
                    <p className="text-xs font-medium text-green-600 uppercase mb-1">Points forts</p>
                    <p className="text-sm text-gray-700">{generationContext.highlights}</p>
                  </div>
                )}
                {generationContext?.lowlights && (
                  <div>
                    <p className="text-xs font-medium text-orange-600 uppercase mb-1">Points d'attention</p>
                    <p className="text-sm text-gray-700">{generationContext.lowlights}</p>
                  </div>
                )}
                {generationContext?.editorial_angle && (
                  <div>
                    <p className="text-xs font-medium text-purple-600 uppercase mb-1">Angle éditorial</p>
                    <p className="text-sm text-gray-700">{generationContext.editorial_angle}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Center Panel - Content Editor */}
          <div className={`${
            expandedPanel === 'content' ? 'col-span-12' : 
            expandedPanel === 'context' || expandedPanel === 'chat' ? 'hidden' :
            showContextPanel ? 'col-span-5' : 'col-span-6'
          } bg-white rounded-lg border overflow-hidden flex flex-col`}>
            <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
              <span className="font-medium text-sm">
                {showPreview ? "Aperçu de l'article" : "Contenu éditable"}
              </span>
              <div className="flex items-center gap-2">
                {!showContextPanel && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowContextPanel(true)}
                  >
                    Afficher contexte
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7"
                  onClick={() => setExpandedPanel(expandedPanel === 'content' ? null : 'content')}
                >
                  {expandedPanel === 'content' ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
                </Button>
              </div>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              {generating ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
                  <p className="text-lg font-medium text-gray-700">Génération en cours...</p>
                  <p className="text-sm text-muted-foreground">L'IA analyse vos photos et crée le contenu</p>
                </div>
              ) : showPreview ? (
                // Preview mode - Magazine layout with content_blocks
                <article className="prose prose-lg max-w-none">
                  {/* Couverture */}
                  {editedContent.featured_image_url && (
                    <div className="mb-8 -mx-4">
                      <img 
                        src={editedContent.featured_image_url} 
                        alt={editedContent.title}
                        className="w-full h-64 object-cover rounded-lg shadow-lg"
                      />
                    </div>
                  )}
                  
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{editedContent.title || "Sans titre"}</h1>
                  {editedContent.subtitle && (
                    <h2 className="text-xl text-gray-600 mb-4">{editedContent.subtitle}</h2>
                  )}
                  
                  {/* Word count indicator - utilise currentWordCount calculé dynamiquement */}
                  {currentWordCount > 0 && (
                    <div className="flex items-center gap-4 mb-6 text-sm text-muted-foreground">
                      <span>📊 {currentWordCount} mots</span>
                      <span>⏱️ ~{Math.ceil(currentWordCount / 200)} min de lecture</span>
                    </div>
                  )}
                  
                  {editedContent.excerpt && (
                    <p className="text-xl italic text-gray-600 border-l-4 border-purple-400 pl-4 mb-8">
                      {editedContent.excerpt}
                    </p>
                  )}
                  
                  {/* Render content_blocks if available */}
                  {editedContent.content_blocks && editedContent.content_blocks.length > 0 ? (
                    <div className="space-y-6">
                      {editedContent.content_blocks.map((block, idx) => {
                        switch (block.type) {
                          case 'section_title':
                            return (
                              <h2 key={idx} className="text-2xl font-bold text-gray-800 mt-10 mb-4 first:mt-0">
                                {block.text}
                              </h2>
                            );
                          case 'paragraph':
                            return (
                              <p key={idx} className="text-gray-700 leading-relaxed">
                                {block.text}
                              </p>
                            );
                          case 'image':
                            return (
                              <figure key={idx} className="my-6">
                                {block.photo_url ? (
                                  <img 
                                    src={block.photo_url} 
                                    alt={block.caption || 'Photo'} 
                                    className="w-full h-auto rounded-lg shadow-md"
                                  />
                                ) : (
                                  <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <Camera className="h-8 w-8 text-gray-400" />
                                  </div>
                                )}
                                {block.caption && (
                                  <figcaption className="mt-2 text-sm text-gray-500 italic text-center">
                                    {block.caption}
                                  </figcaption>
                                )}
                              </figure>
                            );
                          case 'image_gallery':
                            return (
                              <figure key={idx} className="my-6">
                                <div className={`grid gap-3 ${block.photo_urls?.length === 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`}>
                                  {(block.photo_urls || []).map((url, pIdx) => (
                                    <img 
                                      key={pIdx}
                                      src={url} 
                                      alt={`Photo ${pIdx + 1}`} 
                                      className="w-full h-40 object-cover rounded-lg shadow"
                                    />
                                  ))}
                                </div>
                                {block.caption && (
                                  <figcaption className="mt-2 text-sm text-gray-500 italic text-center">
                                    {block.caption}
                                  </figcaption>
                                )}
                              </figure>
                            );
                          case 'quote':
                            return (
                              <blockquote key={idx} className="my-6 pl-6 border-l-4 border-purple-400 italic text-gray-600">
                                {block.text}
                              </blockquote>
                            );
                          default:
                            return null;
                        }
                      })}
                    </div>
                  ) : (
                    // Fallback to plain content
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {editedContent.content || "Aucun contenu"}
                    </div>
                  )}
                </article>
              ) : (
                // Edit mode
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Titre</label>
                    <Input 
                      value={editedContent.title}
                      onChange={e => setEditedContent(prev => ({...prev, title: e.target.value}))}
                      className="text-lg font-semibold"
                      placeholder="Titre de l'article"
                      data-testid="title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Accroche / Résumé</label>
                    <Textarea 
                      value={editedContent.excerpt}
                      onChange={e => setEditedContent(prev => ({...prev, excerpt: e.target.value}))}
                      rows={2}
                      placeholder="Une phrase d'accroche captivante..."
                      data-testid="excerpt-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contenu principal</label>
                    <Textarea 
                      value={editedContent.content}
                      onChange={e => setEditedContent(prev => ({...prev, content: e.target.value}))}
                      rows={expandedPanel === 'content' ? 30 : 18}
                      className="font-mono text-sm leading-relaxed"
                      placeholder="Le contenu de votre article..."
                      data-testid="content-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - AI Chat */}
          <div className={`${
            expandedPanel === 'chat' ? 'col-span-12' : 
            expandedPanel === 'content' || expandedPanel === 'context' ? 'hidden' :
            'col-span-4'
          } bg-white rounded-lg border overflow-hidden flex flex-col`}>
            <div className="p-3 border-b bg-gradient-to-r from-purple-50 to-indigo-50 flex items-center justify-between">
              <span className="font-medium text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-purple-600" />
                Dialogue avec l'IA
              </span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7"
                onClick={() => setExpandedPanel(expandedPanel === 'chat' ? null : 'chat')}
              >
                {expandedPanel === 'chat' ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
              </Button>
            </div>
            
            {/* Cache Indicator */}
            {cacheInfo && (
              <div className={`p-2 border-b ${cacheInfo.fromCache ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs">
                    {cacheInfo.fromCache ? (
                      <>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                          <RefreshCw className="h-3 w-3" />
                          Résultat du cache
                        </span>
                        <span className="text-muted-foreground">
                          Utilisé {cacheInfo.hit_count || 1}x
                        </span>
                      </>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded">
                        <Sparkles className="h-3 w-3" />
                        Nouvelle génération
                      </span>
                    )}
                  </div>
                  {cacheInfo.fromCache && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-xs gap-1 bg-white"
                      onClick={handleForceRegenerate}
                      disabled={generating}
                      data-testid="force-regenerate-btn"
                    >
                      {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                      Forcer regénération
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            {/* Quick Actions */}
            <div className="p-3 border-b bg-gray-50 overflow-x-auto">
              <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Actions rapides</p>
              <div className="flex flex-wrap gap-1.5">
                {quickActions.map((action) => (
                  <Button
                    key={action.id}
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300"
                    onClick={() => handleQuickAction(action)}
                    disabled={chatLoading || generating}
                    data-testid={`quick-action-${action.id}`}
                  >
                    <action.icon className="h-3 w-3" />
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[90%] rounded-lg p-3 ${
                    msg.role === "user" 
                      ? "bg-purple-600 text-white" 
                      : "bg-gray-100 text-gray-900"
                  }`}>
                    <div className="whitespace-pre-wrap text-sm">{msg.content}</div>
                    
                    {/* Suggested changes buttons */}
                    {msg.suggested_changes && msg.suggested_changes.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                        <p className="text-xs font-medium text-gray-500">Appliquer ces modifications :</p>
                        {msg.suggested_changes.map((change, i) => (
                          <Button 
                            key={i}
                            size="sm" 
                            variant="outline"
                            className="w-full justify-start text-left h-auto py-2 bg-white"
                            onClick={() => applyAISuggestion(change)}
                          >
                            <Check className="h-3 w-3 mr-2 shrink-0 text-green-600" />
                            <span className="truncate">{change.description}</span>
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                    <span className="text-sm text-gray-500">L'IA réfléchit...</span>
                  </div>
                </div>
              )}
              
              <div ref={chatEndRef} />
            </div>
            
            {/* Input */}
            <div className="border-t p-3 bg-gray-50">
              <div className="flex gap-2">
                <Textarea
                  value={inputMessage}
                  onChange={e => setInputMessage(e.target.value)}
                  placeholder="Demandez une modification... Ex: 'Rends l'intro plus chaleureuse' ou 'Ajoute plus de détails sur le restaurant'"
                  rows={2}
                  className="resize-none text-sm"
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  data-testid="chat-input"
                />
                <Button 
                  onClick={() => sendMessage()} 
                  disabled={chatLoading || !inputMessage.trim() || generating}
                  className="bg-purple-600 hover:bg-purple-700 h-auto px-4"
                  data-testid="send-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
